/**
 * Sentry Error Tracking Configuration
 * https://docs.sentry.io/platforms/javascript/guides/react/
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for production error tracking
 *
 * Features:
 * - Automatic error capture
 * - Performance monitoring
 * - Session replay (on errors)
 * - Release tracking
 * - Source map support
 */
export const initSentry = () => {
	const dsn = import.meta.env.VITE_SENTRY_DSN;
	const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
	const version = import.meta.env.VITE_APP_VERSION || 'dev';

	// Skip initialization if DSN not configured
	if (!dsn) {
		if (import.meta.env.DEV) {
			console.warn('⚠️ Sentry DSN not configured. Error tracking disabled.');
		}
		return;
	}

	try {
		Sentry.init({
			dsn,
			environment,
			release: `wt-portfolio-cms@${version}`,

			// Integrations
			integrations: [
				// Browser performance tracing
				Sentry.browserTracingIntegration(),

				// Session replay (captures UI interactions)
				Sentry.replayIntegration({
					// Mask all text and media for privacy
					maskAllText: true,
					blockAllMedia: true,
				}),
			],

			// Performance Monitoring
			tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev

			// Session Replay sampling
			replaysSessionSampleRate: 0.1, // 10% of sessions
			replaysOnErrorSampleRate: 1.0, // 100% when errors occur

			// Filter errors before sending
			beforeSend(event, hint) {
				// Don't send errors in development
				if (environment === 'development') {
					console.error(
						'Sentry captured error (not sent in dev):',
						hint.originalException || hint.syntheticException
					);
					return null;
				}

				// Filter out browser extension errors
				if (event.exception) {
					const error = hint.originalException;
					if (error && typeof error === 'object' && 'message' in error) {
						const message = String(error.message);
						if (
							message.includes('chrome-extension://') ||
							message.includes('moz-extension://') ||
							message.includes('safari-extension://')
						) {
							return null;
						}
					}
				}

				return event;
			},

			// Ignore known non-critical errors
			ignoreErrors: [
				// Browser extensions
				'top.GLOBALS',
				'originalCreateNotification',
				'canvas.contentDocument',
				'MyApp_RemoveAllHighlights',
				'atomicFindClose',
				// Network errors (handled gracefully)
				'NetworkError',
				'Failed to fetch',
				'Load failed',
				// ResizeObserver (benign)
				'ResizeObserver loop',
			],

			// Don't track errors from browser extensions
			denyUrls: [
				/extensions\//i,
				/^chrome:\/\//i,
				/^moz-extension:\/\//i,
				/^safari-extension:\/\//i,
			],
		});

		if (import.meta.env.DEV) {
			console.log('✅ Sentry initialized:', { environment, version });
		}
	} catch (error) {
		console.error('Failed to initialize Sentry:', error);
	}
};

/**
 * Set user context when user logs in
 */
export const setUser = (
	user: { id: string; email?: string; name?: string } | null
) => {
	if (user) {
		Sentry.setUser({
			id: user.id,
			email: user.email,
			username: user.name,
		});
	} else {
		Sentry.setUser(null);
	}
};

/**
 * Manually capture an error
 */
export const captureError = (error: Error, context?: Record<string, any>) => {
	if (context) {
		Sentry.setContext('custom', context);
	}
	Sentry.captureException(error);
};

/**
 * Manually capture a message
 */
export const captureMessage = (
	message: string,
	level: 'info' | 'warning' | 'error' = 'info',
	context?: Record<string, any>
) => {
	if (context) {
		Sentry.setContext('custom', context);
	}
	Sentry.captureMessage(message, level);
};
